// Data hooks for the ADMS biometric admin UI.
// Reads go through TanStack Query; live punch/command events arrive over the
// existing socket.io connection and simply invalidate the relevant queries.

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  BiometricDevice,
  BiometricCommand,
  BiometricEmployee,
  UnmappedPin,
  BiometricPunch,
  AttendanceDay,
  BiometricShift,
  BiometricRoster,
  BiometricOpLog,
  Regularisation,
  DeviceHealth,
  ReportEnvelope,
} from '../biometricTypes';
import { localToday } from '@/shared/utils/utils';

const BASE = '/api/biometric/v2';
const KEY = ['biometric-admin'];

// ── Devices ──────────────────────────────────────────────────────────────────

export function useDevices(refetchMs = 15000) {
  return useQuery({
    queryKey: [...KEY, 'devices'],
    queryFn: async () => {
      const { data } = await api.get<{ devices: BiometricDevice[] }>(`${BASE}/devices`);
      return data.devices ?? [];
    },
    refetchInterval: refetchMs,
  });
}

export function useDevice(sn: string | null) {
  return useQuery({
    queryKey: [...KEY, 'device', sn],
    queryFn: async () => {
      const { data } = await api.get<{
        device: BiometricDevice;
        commands: BiometricCommand[];
        oplogs: BiometricOpLog[];
        template_count: number;
      }>(`${BASE}/devices/${sn}`);
      return data;
    },
    enabled: Boolean(sn),
  });
}

export function useDeviceHealth() {
  return useQuery({
    queryKey: [...KEY, 'health'],
    queryFn: async () => {
      const { data } = await api.get<{ devices: DeviceHealth[] }>(`${BASE}/health`);
      return data.devices ?? [];
    },
    refetchInterval: 20000,
  });
}

/** All device actions share one mutation so the UI can drive them generically. */
export function useDeviceActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  return {
    register: useMutation({
      mutationFn: async (payload: Partial<BiometricDevice>) =>
        (await api.post(`${BASE}/devices`, payload)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ sn, patch }: { sn: string; patch: Partial<BiometricDevice> }) =>
        (await api.put(`${BASE}/devices/${sn}`, patch)).data,
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: async (sn: string) => (await api.delete(`${BASE}/devices/${sn}`)).data,
      onSuccess: invalidate,
    }),
    /** Fire any of the POST device command routes: ping, reboot, resync, … */
    command: useMutation({
      mutationFn: async ({ sn, action, body }: { sn: string; action: string; body?: unknown }) =>
        (await api.post(`${BASE}/devices/${sn}/${action}`, body ?? {})).data,
      onSuccess: invalidate,
    }),
    generateKey: useMutation({
      mutationFn: async () => (await api.post<{ key: string }>(`${BASE}/util/generate-key`, {})).data.key,
    }),
  };
}

// ── Employees & enrolment ────────────────────────────────────────────────────

export function useBiometricEmployees() {
  return useQuery({
    queryKey: [...KEY, 'employees'],
    queryFn: async () => {
      const { data } = await api.get<{ employees: BiometricEmployee[] }>(`${BASE}/employees`);
      return data.employees ?? [];
    },
  });
}

export function useUnmappedPins() {
  return useQuery({
    queryKey: [...KEY, 'unmapped'],
    queryFn: async () => {
      const { data } = await api.get<{ unmapped: UnmappedPin[] }>(`${BASE}/unmapped`);
      return data.unmapped ?? [];
    },
  });
}

export function useEmployeeActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  return {
    setMapping: useMutation({
      mutationFn: async ({ userId, biometric_id }: { userId: string; biometric_id: string }) =>
        (await api.put(`${BASE}/employees/${userId}/mapping`, { biometric_id })).data,
      onSuccess: invalidate,
    }),
    pushToDevice: useMutation({
      mutationFn: async ({ userId, sn }: { userId: string; sn?: string }) =>
        (await api.post(`${BASE}/employees/${userId}/push`, sn ? { sn } : {})).data,
      onSuccess: invalidate,
    }),
    removeFromDevice: useMutation({
      mutationFn: async ({ userId, sn }: { userId: string; sn: string }) =>
        (await api.delete(`${BASE}/employees/${userId}/device/${sn}`)).data,
      onSuccess: invalidate,
    }),
    pullTemplates: useMutation({
      mutationFn: async ({ userId, sn }: { userId: string; sn: string }) =>
        (await api.post(`${BASE}/employees/${userId}/templates/pull`, { sn })).data,
      onSuccess: invalidate,
    }),
    mapUnmapped: useMutation({
      mutationFn: async ({ pin, userId }: { pin: string; userId: string }) =>
        (await api.post(`${BASE}/unmapped/${encodeURIComponent(pin)}/map`, { userId })).data,
      onSuccess: invalidate,
    }),
    importEmployees: useMutation({
      mutationFn: async (rows: Record<string, unknown>[]) =>
        (await api.post(`${BASE}/import/employees`, { rows })).data,
      onSuccess: invalidate,
    }),
    importPunches: useMutation({
      mutationFn: async (rows: Record<string, unknown>[]) =>
        (await api.post(`${BASE}/import/punches`, { rows })).data,
      onSuccess: invalidate,
    }),
  };
}

// ── Punches ──────────────────────────────────────────────────────────────────

export interface PunchFilters {
  sn?: string;
  pin?: string;
  from?: string;
  to?: string;
  state?: string;
  verify?: string;
  page?: number;
  limit?: number;
}

export function usePunches(filters: PunchFilters) {
  return useQuery({
    queryKey: [...KEY, 'punches', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '') params.set(k, String(v));
      });
      const { data } = await api.get<{
        punches: BiometricPunch[];
        total: number;
        page: number;
        pages: number;
      }>(`${BASE}/punches?${params}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePunchActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  return {
    addManual: useMutation({
      mutationFn: async (payload: {
        biometric_id: string;
        punch_time: string;
        punch_state?: number;
        device_sn?: string;
      }) => (await api.post(`${BASE}/punches/manual`, payload)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => (await api.delete(`${BASE}/punches/${id}`)).data,
      onSuccess: invalidate,
    }),
  };
}

// ── Attendance ───────────────────────────────────────────────────────────────

export function useAttendanceRange(from: string, to: string, userId?: string) {
  return useQuery({
    queryKey: [...KEY, 'attendance', from, to, userId],
    queryFn: async () => {
      const params = new URLSearchParams({ from, to });
      if (userId) params.set('userId', userId);
      const { data } = await api.get<{ rows: AttendanceDay[] }>(`${BASE}/attendance/range?${params}`);
      return data.rows ?? [];
    },
    enabled: Boolean(from && to),
    placeholderData: keepPreviousData,
  });
}

export function useAttendanceActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  return {
    recompute: useMutation({
      mutationFn: async (payload: { from: string; to?: string; userId?: string }) =>
        (await api.post(`${BASE}/attendance/recompute`, payload)).data,
      onSuccess: invalidate,
    }),
    override: useMutation({
      mutationFn: async ({ id, ...body }: { id: string; status?: string; reason: string }) =>
        (await api.put(`${BASE}/attendance/${id}/override`, body)).data,
      onSuccess: invalidate,
    }),
    lock: useMutation({
      mutationFn: async (month: string) => (await api.post(`${BASE}/attendance/lock`, { month })).data,
      onSuccess: invalidate,
    }),
    unlock: useMutation({
      mutationFn: async (month: string) => (await api.post(`${BASE}/attendance/unlock`, { month })).data,
      onSuccess: invalidate,
    }),
  };
}

// ── Shifts & rosters ─────────────────────────────────────────────────────────

export function useShifts() {
  return useQuery({
    queryKey: [...KEY, 'shifts'],
    queryFn: async () => {
      const { data } = await api.get<{ shifts: BiometricShift[] }>(`${BASE}/shifts`);
      return data.shifts ?? [];
    },
  });
}

export function useRosters() {
  return useQuery({
    queryKey: [...KEY, 'rosters'],
    queryFn: async () => {
      const { data } = await api.get<{ rosters: BiometricRoster[] }>(`${BASE}/rosters`);
      return data.rosters ?? [];
    },
  });
}

export function useShiftActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  return {
    createShift: useMutation({
      mutationFn: async (payload: Partial<BiometricShift>) => (await api.post(`${BASE}/shifts`, payload)).data,
      onSuccess: invalidate,
    }),
    updateShift: useMutation({
      mutationFn: async ({ id, ...patch }: Partial<BiometricShift> & { id: string }) =>
        (await api.put(`${BASE}/shifts/${id}`, patch)).data,
      onSuccess: invalidate,
    }),
    deleteShift: useMutation({
      mutationFn: async (id: string) => (await api.delete(`${BASE}/shifts/${id}`)).data,
      onSuccess: invalidate,
    }),
    createRoster: useMutation({
      mutationFn: async (payload: Record<string, unknown>) => (await api.post(`${BASE}/rosters`, payload)).data,
      onSuccess: invalidate,
    }),
    deleteRoster: useMutation({
      mutationFn: async (id: string) => (await api.delete(`${BASE}/rosters/${id}`)).data,
      onSuccess: invalidate,
    }),
    bulkAssign: useMutation({
      mutationFn: async (payload: { userIds: string[]; shiftId: string; from: string; to?: string }) =>
        (await api.post(`${BASE}/rosters/bulk-assign`, payload)).data,
      onSuccess: invalidate,
    }),
  };
}

// ── Commands, logs, regularisations ──────────────────────────────────────────

export function useCommands(sn?: string, status?: string) {
  return useQuery({
    queryKey: [...KEY, 'commands', sn, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (sn) params.set('sn', sn);
      if (status) params.set('status', status);
      const { data } = await api.get<{ commands: BiometricCommand[] }>(`${BASE}/commands?${params}`);
      return data.commands ?? [];
    },
    refetchInterval: 10000,
  });
}

export function useCommandActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  return {
    retry: useMutation({
      mutationFn: async (id: string) => (await api.post(`${BASE}/commands/${id}/retry`, {})).data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async (id: string) => (await api.delete(`${BASE}/commands/${id}`)).data,
      onSuccess: invalidate,
    }),
  };
}

export function useOpLogs(sn?: string) {
  return useQuery({
    queryKey: [...KEY, 'oplogs', sn],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (sn) params.set('sn', sn);
      const { data } = await api.get<{ oplogs: BiometricOpLog[] }>(`${BASE}/oplogs?${params}`);
      return data.oplogs ?? [];
    },
    refetchInterval: 20000,
  });
}

export function useRegularisations(status?: string) {
  return useQuery({
    queryKey: [...KEY, 'regularisations', status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const { data } = await api.get<{ regularisations: Regularisation[] }>(`${BASE}/regularisations?${params}`);
      return data.regularisations ?? [];
    },
  });
}

export function useRegularisationActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  return {
    raise: useMutation({
      mutationFn: async (payload: Record<string, unknown>) =>
        (await api.post(`${BASE}/regularisations`, payload)).data,
      onSuccess: invalidate,
    }),
    decide: useMutation({
      mutationFn: async ({ id, approve, remarks }: { id: string; approve: boolean; remarks?: string }) =>
        (await api.post(`${BASE}/regularisations/${id}/decide`, { approve, remarks })).data,
      onSuccess: invalidate,
    }),
  };
}

// ── Reports ──────────────────────────────────────────────────────────────────

export function useReport(name: string, params: Record<string, string>, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'report', name, params],
    queryFn: async () => {
      const search = new URLSearchParams({ ...params, format: 'json' });
      const { data } = await api.get<ReportEnvelope>(`${BASE}/reports/${name}?${search}`);
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Trigger a file download for a report in csv / xlsx / pdf. */
export async function downloadReport(
  name: string,
  params: Record<string, string>,
  format: 'csv' | 'xlsx' | 'pdf',
) {
  const search = new URLSearchParams({ ...params, format });
  const res = await api.get(`${BASE}/reports/${name}?${search}`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}-${localToday()}.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ── Live updates ─────────────────────────────────────────────────────────────

/**
 * Refresh the admin queries whenever the server broadcasts a punch, a command
 * transition or a device event. Falls back silently when no socket is present.
 */
export function useBiometricLiveUpdates() {
  const qc = useQueryClient();

  useEffect(() => {
    const socket = (window as unknown as { __zynxSocket?: {
      on: (e: string, cb: () => void) => void;
      off: (e: string, cb: () => void) => void;
    } }).__zynxSocket;
    if (!socket) return;

    const refresh = () => qc.invalidateQueries({ queryKey: KEY });
    const events = ['biometric:punch', 'biometric:command', 'biometric:device', 'biometric:template'];
    events.forEach(e => socket.on(e, refresh));
    return () => events.forEach(e => socket.off(e, refresh));
  }, [qc]);
}
